// src/pages/VideosPage.tsx
import React, { useEffect, useState, useCallback, Fragment, useRef } from 'react';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { getApiBaseUrl } from '../config/api';
import { fetchWithAuth } from '../utils/auth';
import { suspendAccount, restoreAccount } from '../utils/accountActions';
import { ACCOUNT_REASONS, AccountReasonCode, DEFAULT_BULK_REASON, accountActionError, reasonLabel, refusalText, REASON_SHOWN_NOTE } from './live-review/account-reasons';

interface VideoItem {
  id: string;
  url: string;
  [key: string]: any;
}

const PAGE_SIZE = 70;
const STATUSES = ['active', 'failed', 'uploaded', 'deleted', 'blocked', 'muted', 'banned', 'denied', 'review', 'under_review', 'review_required', 'review_failed', 'processing'];

// Account statuses for the USER endpoint (lowercase per API)
type UserAccountStatus = 'active' | 'inactive' | 'suspended' | 'deleted' | 'blocked';
const USER_ACCOUNT_STATUSES: UserAccountStatus[] = ['active', 'inactive', 'suspended', 'deleted', 'blocked'];

const REASONS = [
  { code: 'ACTIVE', label: 'ACTIVE - Safe Video', value: 'ACTIVE - Safe Video: Video is safe' },
  { code: 'OTHERS', label: 'OTHERS - No Comment', value: 'OTHERS - No Comment: Other' },
  { code: 'DUPLICATE', label: 'DUPLICATE - Duplicate Content', value: 'DUPLICATE - Duplicate Content: Video is a duplicate of existing content' },
  { code: 'NSFW', label: 'NSFW - Nudity / Sexual Content', value: 'NSFW - Nudity / Sexual Content: Contains explicit or adult material' },
  {
    code: 'VIOLENCE',
    label: 'VIOLENCE - Violent or Graphic Content',
    value: 'VIOLENCE - Violent or Graphic Content: Shows physical harm or violent behavior',
  },
  {
    code: 'TERROR',
    label: 'TERROR - Terrorist or Extremist Content',
    value: 'TERROR - Terrorist or Extremist Content: Promotes or glorifies terrorism',
  },
  { code: 'HATE', label: 'HATE - Hate Speech', value: 'HATE - Hate Speech: Contains racist, sexist, or discriminatory language' },
  { code: 'HARASS', label: 'HARASS - Bullying or Harassment', value: 'HARASS - Bullying or Harassment: Targets individuals with abusive content' },
  { code: 'DRUGS', label: 'DRUGS - Drugs or Substance Use', value: 'DRUGS - Drugs or Substance Use: Promotes or shows illegal drug use' },
  { code: 'SPAM', label: 'SPAM - Spam or Fake Engagement', value: 'SPAM - Spam or Fake Engagement: Misleading content, scams, or fake activity' },
  {
    code: 'COPYRIGHT',
    label: 'COPYRIGHT - Copyright Violation',
    value: 'COPYRIGHT - Copyright Violation: Uses copyrighted music or video without rights',
  },
  { code: 'MINOR', label: 'MINOR - Exploitation of Minors', value: 'MINOR - Exploitation of Minors: Shows or targets minors in unsafe ways' },
  {
    code: 'FAKE',
    label: 'FAKE - Misleading or Fake Content',
    value: 'FAKE - Misleading or Fake Content: Deliberately misleading or manipulated video',
  },
  { code: 'DANGEROUS', label: 'DANGEROUS - Dangerous Acts', value: 'DANGEROUS - Dangerous Acts: Encourages harmful challenges or unsafe behavior' },
  {
    code: 'SENSITIVE',
    label: 'SENSITIVE - Sensitive or Disturbing Content',
    value: 'SENSITIVE - Sensitive or Disturbing Content: May disturb viewers (e.g., blood, self-harm)',
  },
  { code: 'MALWARE', label: 'MALWARE - Malicious Links', value: 'MALWARE - Malicious Links: Links to harmful or phishing websites' },
  {
    code: 'UNVERIFIED_AD',
    label: 'UNVERIFIED_AD - Unapproved Advertising',
    value: 'UNVERIFIED_AD - Unapproved Advertising: Promotes products without approval',
  },
  {
    code: 'REPEAT_OFFENSE',
    label: 'REPEAT_OFFENSE - Repeat Policy Violation',
    value: 'REPEAT_OFFENSE - Repeat Policy Violation: Repeated uploads of banned content',
  },
];

const VideosPage: React.FC = () => {
  // data + paging
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [page, setPage] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  // filters
  const [reviewedFilter, setReviewedFilter] = useState<'all' | 'true' | 'false'>('false');
  const [videoStatusFilter, setVideoStatusFilter] = useState<string>('active');

  // action dropdown + modal (VIDEO reasons)
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [reasonInput, setReasonInput] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // ===== User account status modal =====
  const [userStatusModalOpen, setUserStatusModalOpen] = useState(false);
  const [userStatus, setUserStatus] = useState<UserAccountStatus | ''>('');
  const [userIdInput, setUserIdInput] = useState<string>('');
  const [userStatusLoading, setUserStatusLoading] = useState(false);
  const [reasonCode, setReasonCode] = useState<AccountReasonCode>(DEFAULT_BULK_REASON);

  // loaders
  const [actionLoading, setActionLoading] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);

  // end / empty-selection handling
  const [noMore, setNoMore] = useState(false); // end of pagination for current selection
  const [selectionEmpty, setSelectionEmpty] = useState(false); // current filter selection has 0 results overall

  // Prefetching
  const [nextPageCache, setNextPageCache] = useState<VideoItem[]>([]);
  const prefetchingRef = useRef(false);

  // Quick Video Search
  const [searchVideoId, setSearchVideoId] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // Action feedback indicator
  const [lastAction, setLastAction] = useState<{ action: string; timestamp: number } | null>(null);

  const selectionLabel = useCallback(() => {
    const reviewed = reviewedFilter === 'all' ? 'all' : reviewedFilter === 'true' ? 'reviewed' : 'unreviewed';
    const status = videoStatusFilter ? videoStatusFilter.replace('_', ' ') : 'all statuses';
    return `${reviewed} • ${status}`;
  }, [reviewedFilter, videoStatusFilter]);

  const loadVideos = useCallback(
    async (newPage: number, resetIndex = true, options?: { suppressEmpty?: boolean; preserveOnEmpty?: boolean; useCache?: boolean }) => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('No token found');
        return [] as VideoItem[];
      }

      // Check if we can use cached next page
      if (options?.useCache && nextPageCache.length > 0 && newPage === page + 1) {
        setVideos(nextPageCache);
        setPage(newPage);
        setNoMore(false);
        setSelectionEmpty(false);
        if (resetIndex) setCurrentIndex(0);
        setNextPageCache([]);
        return nextPageCache;
      }

      setLoading(true);
      try {
        const body: any = { From: newPage, BatchSize: PAGE_SIZE };
        if (reviewedFilter !== 'all') body.Reviewed = reviewedFilter === 'true';
        if (videoStatusFilter) body.VideoStatus = videoStatusFilter;

        const res = await fetch(`${getApiBaseUrl()}/video/t/findAllVideos`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify(body),
        });
        const json = await res.json();
        if (!res.ok) {
          const msg = json && (json as any).message ? String((json as any).message) : '';
          // Treat 404 or "No videos found..." as an empty selection instead of an error
          if (res.status === 404 || (msg && msg.toLowerCase().includes('no videos'))) {
            setNoMore(true);
            setSelectionEmpty(true);
            toast.info(`There are no videos in your selection — ${selectionLabel()}. Change filters to continue.`);
            return [] as VideoItem[];
          }
          // Anything else is a real failure
          throw new Error(msg || 'Failed to load');
        }
        const items: VideoItem[] = (json.data.videosResponse || [])
          .map((v: any) => {
            const firstResolution =
              Array.isArray(v?.VideoResolutions) && v.VideoResolutions.length > 0
                ? v.VideoResolutions.find((res: any) => typeof res === 'string' && res.trim())
                : undefined;
            return {
              ...v,
              id: v._id,
              // Prefer the highest resolution provided, fall back to the original URL.
              url: firstResolution || v.VideoUrl,
            };
          })
          .filter((v: any) => v.id && v.url);

        // Handle "completely empty selection" or empty page
        const isActuallyEmpty = items.length === 0;
        if (isActuallyEmpty && (options?.suppressEmpty || options?.preserveOnEmpty)) {
          // Keep current UI intact, disable Next, and show a toast
          setNoMore(true);
          setSelectionEmpty(true);
          toast.info(
            options?.suppressEmpty
              ? `You have reviewed all — your selection (${selectionLabel()}).`
              : `There are no more videos in your selection — ${selectionLabel()}. Change filters to continue.`
          );
          return [] as VideoItem[];
        }

        // Normal path: replace the page data
        setVideos(items);
        setPage(newPage);
        setNoMore(false);
        setSelectionEmpty(false);
        if (resetIndex) setCurrentIndex(0);
        else setCurrentIndex((i) => Math.min(i, Math.max(items.length - 1, 0)));

        return items;
      } catch (e: any) {
        toast.error(e.message);
        return [] as VideoItem[];
      } finally {
        setLoading(false);
      }
    },
    [reviewedFilter, videoStatusFilter, selectionLabel, nextPageCache, page]
  );

  // Prefetch next page
  const prefetchNextPage = useCallback(async () => {
    if (prefetchingRef.current || noMore || selectionEmpty) return;

    const token = localStorage.getItem('authToken');
    if (!token) return;

    prefetchingRef.current = true;
    try {
      const body: any = { From: page + 1, BatchSize: PAGE_SIZE };
      if (reviewedFilter !== 'all') body.Reviewed = reviewedFilter === 'true';
      if (videoStatusFilter) body.VideoStatus = videoStatusFilter;

      const res = await fetch(`${getApiBaseUrl()}/video/t/findAllVideos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        const json = await res.json();
        const items: VideoItem[] = (json.data.videosResponse || [])
          .map((v: any) => {
            const firstResolution =
              Array.isArray(v?.VideoResolutions) && v.VideoResolutions.length > 0
                ? v.VideoResolutions.find((res: any) => typeof res === 'string' && res.trim())
                : undefined;
            return {
              ...v,
              id: v._id,
              url: firstResolution || v.VideoUrl,
            };
          })
          .filter((v: any) => v.id && v.url);

        if (items.length > 0) {
          setNextPageCache(items);
        }
      }
    } catch (e) {
      // Silently fail prefetch
    } finally {
      prefetchingRef.current = false;
    }
  }, [page, reviewedFilter, videoStatusFilter, noMore, selectionEmpty]);

  useEffect(() => {
    // When filters change or on first load, preserve UI on empty selections
    loadVideos(1, true, { preserveOnEmpty: true });
    setNextPageCache([]); // Clear cache when filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewedFilter, videoStatusFilter]);

  // Prefetch when video status filter changes or when reaching second half of current page
  useEffect(() => {
    if (videos.length > 0 && currentIndex >= videos.length / 2) {
      prefetchNextPage();
    }
  }, [currentIndex, videos.length, prefetchNextPage]);

  // Auto-clear action indicator after 3 seconds
  useEffect(() => {
    if (lastAction) {
      const timer = setTimeout(() => {
        setLastAction(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [lastAction]);

  // Helper: ObjectId validation for UX
  const isValidObjectId = useCallback((s: string) => /^[a-fA-F0-9]{24}$/.test(s), []);

  // Quick Video Search Handler
  const handleFetchVideoById = async () => {
    if (!searchVideoId.trim()) {
      toast.error('Please enter a video ID');
      return;
    }
    if (!isValidObjectId(searchVideoId.trim())) {
      toast.error('Invalid video ID format. Expected 24-character hex string.');
      return;
    }
    setSearchLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('No token found');

      const targetId = searchVideoId.trim();
      let foundVideo = null;

      // Try fetching multiple pages to find the video
      const maxPages = 10;
      const batchSize = 100;

      toast.info('Searching for video...');

      for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
        const res = await fetch(`${getApiBaseUrl()}/video/t/findAllVideos`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({
            From: pageNum,
            BatchSize: batchSize
          }),
        });

        const json = await res.json();
        if (!res.ok) {
          throw new Error(json.message || 'Failed to fetch videos');
        }

        // Filter client-side to find the exact video
        const allVideos = json.data?.videosResponse || [];
        foundVideo = allVideos.find((v: any) => v._id === targetId);

        if (foundVideo) {
          break; // Found it!
        }

        // If we got fewer results than batch size, we've reached the end
        if (allVideos.length < batchSize) {
          break;
        }
      }

      if (!foundVideo) {
        toast.error(`Video not found with ID: ${targetId}. Searched ${maxPages * batchSize} videos.`);
        setSearchLoading(false);
        return;
      }

      // Load the fetched video into the viewer
      const firstResolution =
        Array.isArray(foundVideo?.VideoResolutions) && foundVideo.VideoResolutions.length > 0
          ? foundVideo.VideoResolutions.find((res: any) => typeof res === 'string' && res.trim())
          : undefined;

      const videoItem: VideoItem = {
        ...foundVideo,
        id: foundVideo._id,
        url: firstResolution || foundVideo.VideoUrl,
      };

      // Replace current videos with searched video
      setVideos([videoItem]);
      setCurrentIndex(0);
      setPage(1);
      setNoMore(true);
      setSelectionEmpty(false);
      toast.success('Video loaded successfully');
      setSearchVideoId('');
    } catch (err: any) {
      toast.error('Failed to fetch video: ' + err.message);
    } finally {
      setSearchLoading(false);
    }
  };

  // When user picks an action from the action dropdown (VIDEO):
  const onActionSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const action = e.target.value;
    if (!action) return;
    setSelectedAction(action);
    setReasonInput('');
    setIsModalOpen(true);
  };

  // Open the USER status modal, prefill UserID from current video if present
  const openUserStatusModal = () => {
    const defaultId = (videos[currentIndex] && ((videos[currentIndex] as any).UserID || (videos[currentIndex] as any).UserId)) || '';
    setUserIdInput(defaultId);
    setUserStatus('');
    setReasonCode(DEFAULT_BULK_REASON);
    setUserStatusModalOpen(true);
  };

  // Auto-advance to next video
  const advanceToNext = async () => {
    if (currentIndex < videos.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      // Try to load next page with cache
      await loadVideos(page + 1, true, { suppressEmpty: true, useCache: true });
    }
  };

  const updateUserAccountStatus = async () => {
    const targetId = userIdInput.trim();
    if (!targetId || !userStatus) return;
    setUserStatusLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('No token found');

      if (userStatus === 'suspended') {
        // Gated route with the reason the person is shown. Only "already suspended" carries on; any other refusal
        // throws here, so nothing is reported as done and no video is blocked.
        const out = await suspendAccount(targetId, reasonCode, `Videos review [${reasonCode}]: status set to suspended`);
        if (out.already) toast.info(out.message);
        else if (out.revocation) (out.revocation.warn ? toast.warn : toast.info)(out.revocation.text);
      } else if (userStatus === 'active') {
        const out = await restoreAccount(targetId, 'Videos review: status set to active');
        if (out.via === 'legacy' && out.message) toast.info(out.message);
      } else {
        const res = await fetchWithAuth(`${getApiBaseUrl()}/user/t/updateUserAccountStatus`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ UserID: targetId, AccountStatus: userStatus }),
        });

        const j = await res.json();
        if (!res.ok) throw new Error(refusalText(res.status, j?.message, 'Failed to update account status'));
      }

      toast.success(`User status set to ${userStatus}`);
      setUserStatusModalOpen(false);

      // If user is suspended or blocked, auto-block the current video with NSFW reason
      if ((userStatus === 'suspended' || userStatus === 'blocked') && videos[currentIndex]) {
        toast.info('Auto-blocking video with NSFW reason...');

        // Block video with NSFW reason
        try {
          const videoRes = await fetchWithAuth(`${getApiBaseUrl()}/video/t/updateVideo`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              ItemID: videos[currentIndex].id,
              Action: 'blocked',
              VideoStatusReason:
                userStatus === 'suspended' && reasonCode !== DEFAULT_BULK_REASON
                  ? reasonLabel(reasonCode)
                  : 'NSFW - Nudity / Sexual Content: Contains explicit or adult material',
            }),
          });

          const videoJson = await videoRes.json();
          if (videoRes.ok) {
            toast.success('Video blocked successfully');
            // Auto-advance to next video
            await advanceToNext();
          } else {
            toast.error(refusalText(videoRes.status, videoJson.message, 'Failed to block video, but user status updated'));
          }
        } catch (err: any) {
          toast.error('Failed to auto-block video: ' + err.message);
        }
      }
    } catch (err: any) {
      toast.error(accountActionError(err));
    } finally {
      setUserStatusLoading(false);
    }
  };

  // Actually call updateVideo for block/delete/other
  const confirmAction = async (overrideAction?: string, overrideReason?: string, autoAdvance = false) => {
    setActionLoading(true);
    setIsModalOpen(false);
    try {
      const current = videos[currentIndex];
      if (!current) {
        throw new Error('No video selected');
      }

      const res = await fetchWithAuth(`${getApiBaseUrl()}/video/t/updateVideo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ItemID: current.id,
          Action: (overrideAction ?? (selectedAction || '')).toLowerCase(),
          VideoStatusReason: overrideReason ?? reasonInput,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(refusalText(res.status, j.message, 'Failed to update video'));

      const actionTaken = (overrideAction || selectedAction || '').toUpperCase();
      toast.success(`Video ${actionTaken}`);
      setLastAction({ action: actionTaken, timestamp: Date.now() });

      // Auto-advance to next video if requested
      if (autoAdvance) {
        await advanceToNext();
      } else {
        await loadVideos(page, true);
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
      setSelectedAction('');
    }
  };

  // wired-up block/delete shortcuts
  const blockVideo = () => {
    setSelectedAction('blocked');
    // Set default reason based on video status
    const currentVideoStatus = videos[currentIndex]?.VideoStatus?.toLowerCase();
    if (currentVideoStatus === 'review_required') {
      setReasonInput('NSFW - Nudity / Sexual Content: Contains explicit or adult material');
    } else {
      setReasonInput('VIOLENCE - Violent or Graphic Content: Shows physical harm or violent behavior');
    }
    setIsModalOpen(true);
  };
  const deleteVideo = () => {
    setSelectedAction('deleted');
    setReasonInput('');
    setIsModalOpen(true);
  };
  const activeVideo = async () => {
    if (actionLoading || loading || reviewLoading) return;
    await confirmAction('active', 'Active - Safe Video', true); // Auto-advance after approval
  };

  // navigation + review-on-next
  const prev = () => {
    if (loading || actionLoading || reviewLoading) return;
    if (selectionEmpty) return; // nothing to navigate
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    } else if (page > 1) {
      setNoMore(false); // moving backward implies there are more ahead again
      loadVideos(page - 1, false);
    }
  };

  const next = async () => {
    if (loading || actionLoading || reviewLoading) return;
    if (selectionEmpty) return; // nothing to navigate
    const current = videos[currentIndex];
    if (!current) return;

    // 1) mark reviewed
    setReviewLoading(true);
    try {
      const res = await fetchWithAuth(`${getApiBaseUrl()}/video/t/updateVideo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ItemID: current.id }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(refusalText(res.status, err.message, 'Failed to mark reviewed'));
      }
    } catch (err: any) {
      toast.error(err.message);
      setReviewLoading(false);
      return;
    }
    setReviewLoading(false);

    // 2) advance to next
    await advanceToNext();
  };

  // === UI ===
  if (loading && !videos.length && !selectionEmpty) return <p className='mt-10 text-center'>Loading…</p>;

  const hasCurrent = videos.length > 0;
  const current = hasCurrent ? videos[currentIndex] : ({} as any);
  const videoSrc =
    hasCurrent && !selectionEmpty
      ? `${current.UploadedFromR2 === true ? 'https://cdn2.pingtop.co' : 'https://cdn.pingtop.co'}${current.url}`
      : undefined;

  const isPrevDisabled = selectionEmpty || !hasCurrent || currentIndex === 0 || loading || actionLoading || reviewLoading;
  const isNextDisabled = selectionEmpty || !hasCurrent || loading || actionLoading || reviewLoading || (currentIndex === videos.length - 1 && noMore);

  return (
    <Fragment>
      <Navbar />
      <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
        {/* Background decoration */}
        <div className='fixed inset-0 overflow-hidden pointer-events-none'>
          <div className='absolute -top-40 -right-40 w-80 h-80 bg-[#FF9C15]/10 rounded-full blur-3xl' />
          <div className='absolute -bottom-40 -left-40 w-80 h-80 bg-[#F42026]/10 rounded-full blur-3xl' />
        </div>

        {/* Header */}
        <div className='relative bg-white/5 backdrop-blur-sm border-b border-white/10'>
          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between'>
              <div>
                <h1 className='text-3xl font-bold text-white'>Video Moderation</h1>
                <p className='mt-1 text-sm text-gray-400'>Review and moderate video content</p>
              </div>
              <div className='mt-4 sm:mt-0 flex items-center gap-3'>
                <div className='flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg border border-white/10'>
                  <span className='text-sm font-medium text-gray-300'>Page</span>
                  <span className='inline-flex items-center justify-center min-w-[2rem] h-8 px-2 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-bold rounded-md'>
                    {page}
                  </span>
                </div>
                <div className='flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg border border-white/10'>
                  <span className='text-sm font-medium text-gray-300'>Videos</span>
                  <span className='inline-flex items-center justify-center min-w-[2rem] h-8 px-2 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-bold rounded-md'>
                    {videos.length}
                  </span>
                </div>
                {nextPageCache.length > 0 && (
                  <div className='flex items-center gap-2 bg-white/5 px-4 py-2 rounded-lg border border-white/10'>
                    <span className='text-sm font-medium text-gray-300'>Cached</span>
                    <span className='inline-flex items-center justify-center min-w-[2rem] h-8 px-2 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white font-bold rounded-md'>
                      {nextPageCache.length}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
          {/* Quick Video Search */}
          <div className='bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 mb-6 overflow-hidden'>
            <div className='bg-gradient-to-r from-[#FF9C15] to-[#F42026] px-6 py-4'>
              <h2 className='text-lg font-semibold text-white flex items-center gap-2'>
                <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
                  <path
                    fillRule='evenodd'
                    d='M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z'
                    clipRule='evenodd'
                  />
                </svg>
                Quick Video Search
              </h2>
              <p className='text-sm text-orange-50 mt-1'>Search and load any video by its ID</p>
            </div>
            <div className='p-6'>
              <div className='flex flex-col sm:flex-row gap-3'>
                <div className='flex-1'>
                  <input
                    type='text'
                    value={searchVideoId}
                    onChange={(e) => setSearchVideoId(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') handleFetchVideoById();
                    }}
                    placeholder='Enter Video ID (e.g., 66e9c57b5f042a244c7057d6)'
                    className='w-full px-4 py-3 bg-white/10 border-2 border-white/20 rounded-lg focus:ring-2 focus:ring-[#FF9C15] focus:border-[#FF9C15] transition-all font-mono text-sm text-white placeholder-gray-400'
                    disabled={searchLoading}
                  />
                  {searchVideoId && !isValidObjectId(searchVideoId) && (
                    <p className='text-xs text-red-400 mt-2'>Invalid format. Expected 24-character hex string.</p>
                  )}
                </div>
                <button
                  onClick={handleFetchVideoById}
                  disabled={searchLoading || !searchVideoId.trim() || !isValidObjectId(searchVideoId)}
                  className='px-8 py-3 bg-gradient-to-r from-[#FF9C15] to-[#F42026] hover:from-[#e88c13] hover:to-[#d91c22] text-white font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2'
                >
                  {searchLoading ? (
                    <>
                      <svg className='animate-spin h-5 w-5' fill='none' viewBox='0 0 24 24'>
                        <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                        <path
                          className='opacity-75'
                          fill='currentColor'
                          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                        />
                      </svg>
                      Searching...
                    </>
                  ) : (
                    <>
                      <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
                        <path
                          fillRule='evenodd'
                          d='M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z'
                          clipRule='evenodd'
                        />
                      </svg>
                      Fetch Video
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className='bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 mb-6 overflow-hidden'>
            <div className='bg-gradient-to-r from-[#FF9C15]/20 to-[#F42026]/20 px-6 py-4 border-b border-white/10'>
              <h2 className='text-lg font-semibold text-white'>Filters</h2>
            </div>
            <div className='p-6'>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-300 mb-2'>Review Status</label>
                  <select
                    value={reviewedFilter}
                    onChange={(e) => setReviewedFilter(e.target.value as any)}
                    className='w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-[#FF9C15] focus:border-[#FF9C15] transition-all text-white'
                  >
                    <option value='all' className='bg-slate-800'>📋 All Videos</option>
                    <option value='true' className='bg-slate-800'>✅ Reviewed</option>
                    <option value='false' className='bg-slate-800'>⏳ Unreviewed</option>
                  </select>
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-300 mb-2'>Video Status</label>
                  <select
                    value={videoStatusFilter}
                    onChange={(e) => setVideoStatusFilter(e.target.value)}
                    className='w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg focus:ring-2 focus:ring-[#FF9C15] focus:border-[#FF9C15] transition-all text-white'
                  >
                    <option value=''>All Statuses</option>
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s.replace('_', ' ').toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Video Player Section */}
          <div className='bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden'>
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-0'>
              {/* Video Player - Left/Top */}
              <div className='lg:col-span-1 bg-gradient-to-br from-gray-900 to-slate-900 p-6 flex flex-col items-center justify-center'>
                <div className='w-full max-w-[360px] mx-auto'>
                  {/* User Info Header - Username and Country side by side */}
                  {hasCurrent && !selectionEmpty && (
                    <div className='mb-3 bg-gray-800/90 backdrop-blur-sm rounded-xl p-3 shadow-lg'>
                      <div className='grid grid-cols-2 gap-3'>
                        {/* Username */}
                        <div className='flex items-center gap-2 min-w-0'>
                          <div className='flex items-center justify-center w-7 h-7 bg-blue-500 rounded-full flex-shrink-0'>
                            <svg className='w-4 h-4 text-white' fill='currentColor' viewBox='0 0 20 20'>
                              <path
                                fillRule='evenodd'
                                d='M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z'
                                clipRule='evenodd'
                              />
                            </svg>
                          </div>
                          <div className='flex-1 min-w-0'>
                            <p className='text-xs text-gray-400 font-medium'>User</p>
                            <p className='text-sm font-bold text-white truncate'>
                              {current.UserName || current.Username || 'Unknown'}
                            </p>
                          </div>
                        </div>

                        {/* Country */}
                        <div className='flex items-center gap-2 min-w-0'>
                          <div className='flex items-center justify-center w-7 h-7 bg-green-500 rounded-full flex-shrink-0'>
                            <svg className='w-4 h-4 text-white' fill='currentColor' viewBox='0 0 20 20'>
                              <path
                                fillRule='evenodd'
                                d='M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z'
                                clipRule='evenodd'
                              />
                            </svg>
                          </div>
                          <div className='flex-1 min-w-0'>
                            <p className='text-xs text-gray-400 font-medium'>Country</p>
                            <p className='text-sm font-bold text-white truncate'>{current.Country || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Phone Frame with Video Type Badge */}
                  <div className='relative'>
                    <div className='aspect-[9/16] w-full max-w-[360px] mx-auto bg-black rounded-3xl shadow-2xl overflow-hidden border-4 border-gray-800 relative'>
                      {videoSrc ? (
                        <>
                          <video src={videoSrc} controls autoPlay loop className='w-full h-full object-cover' />
                          {/* Action Indicator - Top Left Corner */}
                          {lastAction && (
                            <div className='absolute top-3 left-3 z-20 animate-pulse'>
                              <div
                                className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-2xl border-2 border-white/30 font-bold text-white text-sm ${
                                  lastAction.action === 'ACTIVE' || lastAction.action === 'APPROVED'
                                    ? 'bg-gradient-to-r from-green-500 to-emerald-600'
                                    : lastAction.action === 'BLOCKED'
                                    ? 'bg-gradient-to-r from-yellow-500 to-orange-600'
                                    : lastAction.action === 'DELETED'
                                    ? 'bg-gradient-to-r from-red-500 to-red-700'
                                    : 'bg-gradient-to-r from-blue-500 to-indigo-600'
                                }`}
                              >
                                {lastAction.action === 'ACTIVE' || lastAction.action === 'APPROVED' ? (
                                  <span>✅ APPROVED</span>
                                ) : lastAction.action === 'BLOCKED' ? (
                                  <span>🚫 BLOCKED</span>
                                ) : lastAction.action === 'DELETED' ? (
                                  <span>🗑️ DELETED</span>
                                ) : (
                                  <span>📋 {lastAction.action}</span>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Video Type Badge - Top Right Corner */}
                          {hasCurrent && !selectionEmpty && (
                            <div className='absolute top-3 right-3 z-10'>
                              {current.VideoType?.toUpperCase() === 'PINGS' || current.VideoType?.toUpperCase() === 'PING' ? (
                                <div className='flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full shadow-2xl border-2 border-white/30'>
                                  {/* Lightning Bolt Icon for PING - instant/notification */}
                                  <svg className='w-7 h-7 text-white' fill='currentColor' viewBox='0 0 20 20'>
                                    <path
                                      fillRule='evenodd'
                                      d='M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z'
                                      clipRule='evenodd'
                                    />
                                  </svg>
                                </div>
                              ) : current.VideoType?.toUpperCase() === 'STORIES' || current.VideoType?.toUpperCase() === 'STORY' ? (
                                <div className='flex items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full shadow-2xl border-2 border-white/30'>
                                  {/* Book Open Icon for STORY - storytelling */}
                                  <svg className='w-7 h-7 text-white' fill='currentColor' viewBox='0 0 20 20'>
                                    <path d='M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z' />
                                  </svg>
                                </div>
                              ) : (
                                <div className='flex items-center justify-center w-14 h-14 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full shadow-2xl border-2 border-white/30'>
                                  <svg className='w-7 h-7 text-white' fill='currentColor' viewBox='0 0 20 20'>
                                    <path d='M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z' />
                                  </svg>
                                  {/* Debug - show actual VideoType */}
                                  <div className='absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-black/80 text-white text-xs px-2 py-1 rounded whitespace-nowrap'>
                                    {current.VideoType || 'null'}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className='absolute inset-0 flex items-center justify-center text-center p-6'>
                          <div>
                            <div className='text-6xl mb-4'>📹</div>
                            <p className='text-white text-sm font-medium'>
                              {selectionEmpty ? `No videos match your selection` : 'Select filters to start reviewing'}
                            </p>
                            <p className='text-gray-400 text-xs mt-2'>{selectionLabel()}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Navigation Controls */}
                  <div className='mt-6 flex items-center justify-between gap-3'>
                    <button
                      onClick={prev}
                      disabled={isPrevDisabled}
                      className='flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed'
                    >
                      ← Prev
                    </button>
                    <div className='flex items-center gap-2 px-4 py-3 bg-gray-800 rounded-xl'>
                      <span className='text-white text-sm font-medium'>
                        {currentIndex + 1} / {videos.length || 0}
                      </span>
                    </div>
                    <button
                      onClick={next}
                      disabled={isNextDisabled}
                      className='flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed'
                      title={
                        selectionEmpty
                          ? `No videos in selection`
                          : noMore && currentIndex === videos.length - 1
                          ? `All reviewed`
                          : 'Next'
                      }
                    >
                      {reviewLoading ? 'Reviewing...' : 'Next →'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Controls - Right/Bottom */}
              <div className='lg:col-span-2 p-6'>
                <div className='space-y-6'>
                  {/* Quick Actions */}
                  <div>
                    <h3 className='text-lg font-semibold text-gray-800 mb-4'>Quick Actions</h3>
                    <div className='grid grid-cols-2 sm:grid-cols-4 gap-3'>
                      <button
                        onClick={activeVideo}
                        disabled={selectionEmpty || !hasCurrent || actionLoading || loading || reviewLoading}
                        className='flex flex-col items-center justify-center p-4 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed'
                      >
                        <span className='text-3xl mb-2'>✅</span>
                        <span className='text-sm font-semibold'>Approve</span>
                      </button>
                      <button
                        onClick={blockVideo}
                        disabled={selectionEmpty || !hasCurrent || actionLoading || loading || reviewLoading}
                        className='flex flex-col items-center justify-center p-4 bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-xl hover:from-yellow-600 hover:to-yellow-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed'
                      >
                        <span className='text-3xl mb-2'>🚫</span>
                        <span className='text-sm font-semibold'>Block</span>
                      </button>
                      <button
                        onClick={deleteVideo}
                        disabled={selectionEmpty || !hasCurrent || actionLoading || loading || reviewLoading}
                        className='flex flex-col items-center justify-center p-4 bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed'
                      >
                        <span className='text-3xl mb-2'>🗑️</span>
                        <span className='text-sm font-semibold'>Delete</span>
                      </button>
                      <button
                        onClick={openUserStatusModal}
                        disabled={selectionEmpty || loading || actionLoading || reviewLoading}
                        className='flex flex-col items-center justify-center p-4 bg-gradient-to-br from-indigo-500 to-indigo-600 text-white rounded-xl hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed'
                      >
                        <span className='text-3xl mb-2'>👤</span>
                        <span className='text-sm font-semibold'>User Status</span>
                      </button>
                    </div>
                  </div>

                  {/* Other Status */}
                  <div>
                    <h3 className='text-lg font-semibold text-gray-800 mb-4'>Other Status</h3>
                    <select
                      value={selectedAction}
                      onChange={onActionSelect}
                      disabled={selectionEmpty || !hasCurrent || actionLoading || loading || reviewLoading}
                      className='w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:opacity-40 disabled:cursor-not-allowed font-medium'
                    >
                      <option value=''>Select a status...</option>
                      {STATUSES.map((s) => (
                        <option key={s} value={s.toUpperCase()}>
                          {s.replace('_', ' ').toUpperCase()}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Video Properties */}
                  {hasCurrent && !selectionEmpty && (
                    <div>
                      <h3 className='text-lg font-semibold text-gray-800 mb-4'>Video Properties</h3>
                      <div className='bg-gray-50 rounded-xl border border-gray-200 overflow-hidden max-h-[400px] overflow-y-auto'>
                        <table className='w-full text-sm'>
                          <tbody className='divide-y divide-gray-200'>
                            {Object.entries(current)
                              .filter(([k]) => k !== 'id' && k !== 'url')
                              .map(([k, v]) => (
                                <tr key={k} className='hover:bg-gray-100 transition-colors'>
                                  <td className='px-4 py-3 font-semibold text-gray-700 w-1/3'>{k}</td>
                                  <td className='px-4 py-3 text-gray-900 break-all'>{String(v)}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reason Modal */}
      {isModalOpen && (
        <div className='fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-2xl shadow-2xl w-full max-w-lg transform transition-all'>
            <div className='bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-2xl'>
              <h3 className='text-xl font-bold text-white'>Select Reason</h3>
              <p className='text-blue-100 text-sm mt-1'>Action: {selectedAction}</p>
            </div>
            <div className='p-6 space-y-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>Predefined Reasons</label>
                <select
                  value={reasonInput}
                  onChange={(e) => setReasonInput(e.target.value)}
                  className='w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all'
                >
                  <option value=''>Select a reason...</option>
                  {REASONS.map((r) => (
                    <option key={r.code} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>Custom Reason</label>
                <textarea
                  rows={4}
                  value={reasonInput}
                  onChange={(e) => setReasonInput(e.target.value)}
                  className='w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none'
                  placeholder='Enter a custom reason...'
                />
              </div>
              <div className='flex items-center mb-2'>
                <input
                  type='checkbox'
                  id='auto-advance'
                  defaultChecked
                  className='mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                />
                <label htmlFor='auto-advance' className='text-sm text-gray-700'>
                  Auto-advance to next video after action
                </label>
              </div>
              <div className='flex justify-end gap-3 pt-2'>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className='px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-all'
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const checkbox = document.getElementById('auto-advance') as HTMLInputElement;
                    confirmAction(selectedAction, reasonInput, checkbox?.checked ?? true);
                  }}
                  disabled={!reasonInput.trim()}
                  className='px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md'
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Status Modal */}
      {userStatusModalOpen && (
        <div className='fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-2xl shadow-2xl w-full max-w-lg transform transition-all'>
            <div className='bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-2xl'>
              <h3 className='text-xl font-bold text-white'>Update User Account Status</h3>
              <p className='text-indigo-100 text-sm mt-1'>Suspending/blocking user will auto-block video with NSFW reason</p>
            </div>
            <div className='p-6 space-y-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>User ID</label>
                <input
                  value={userIdInput}
                  onChange={(e) => setUserIdInput(e.target.value)}
                  placeholder='66e9c57b5f042a244c7057d6'
                  className='w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-mono'
                  spellCheck={false}
                />
                {!isValidObjectId(userIdInput.trim()) && userIdInput && (
                  <p className='text-xs text-red-600 mt-2'>Expected a 24-character hex MongoDB ObjectId</p>
                )}
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>Account Status</label>
                <select
                  value={userStatus}
                  onChange={(e) => setUserStatus(e.target.value as UserAccountStatus)}
                  className='w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all'
                >
                  <option value=''>Select status...</option>
                  {USER_ACCOUNT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {userStatus === 'suspended' && (
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>Reason shown to the user</label>
                  <select
                    value={reasonCode}
                    onChange={(e) => setReasonCode(e.target.value as AccountReasonCode)}
                    className='w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all'
                  >
                    {ACCOUNT_REASONS.map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <p className='text-xs text-gray-500 mt-1'>{REASON_SHOWN_NOTE}</p>
                  <p className='text-xs text-orange-700 mt-1'>Suspending also bans the person&apos;s phones.</p>
                </div>
              )}

              {(userStatus === 'suspended' || userStatus === 'blocked') && (
                <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-3'>
                  <p className='text-sm text-yellow-800'>
                    ⚠️ This will automatically block the current video with NSFW reason and advance to the next video.
                  </p>
                </div>
              )}

              <div className='flex justify-end gap-3 pt-2'>
                <button
                  onClick={() => setUserStatusModalOpen(false)}
                  className='px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-all'
                >
                  Cancel
                </button>
                <button
                  onClick={updateUserAccountStatus}
                  disabled={!isValidObjectId(userIdInput.trim()) || !userStatus || userStatusLoading}
                  className='px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md'
                >
                  {userStatusLoading ? 'Updating...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default VideosPage;
